<template>
  <b-modal
      ref="updateModal"
      v-model="isOpen"
      centered
      size="md"
      modal-class="update-level-modal"
      header-class="modal-header"
      body-class="modal-body"
      footer-class="modal-footer"
      hide-header-close
      @hidden="handleClose"
  >
    <!-- Title -->
    <template #modal-title>
      <div class="modal-titlebar">
        <div class="modal-titlebar__icon">
          <feather-icon icon="EditIcon" size="20" />
        </div>
        <div class="modal-titlebar__text">ویرایش سطح وفاداری</div>
      </div>
    </template>

    <!-- User info card -->
    <div v-if="formData.userName" class="user-info-card">
      <div class="user-info__left">
        <div class="user-info__avatar">
          <feather-icon icon="UserIcon" size="20" />
        </div>

        <div class="user-info__details">
          <div class="user-info__name">{{ formData.userName }}</div>
          <div class="user-info__mobile">{{ formData.userMobile }}</div>
        </div>
      </div>

      <div class="user-info__right">
        <div class="user-info__current-label">سطح فعلی</div>
        <div class="user-info__current">
          <LevelAvatarBadge :avatarKey="formData.currentLevelAvatar" size="sm" />
          <span class="user-info__current-value">{{ formData.currentLevelName }}</span>
        </div>
      </div>
    </div>

    <!-- Form -->
    <b-row>
      <b-col cols="12">
        <div class="f-group">
          <label class="f-label required">
            <feather-icon icon="AwardIcon" size="14" />
            انتخاب سطح جدید
          </label>

          <!-- CustomPicker (replaces v-select) -->
          <CustomPicker
              v-model="selectedLevel"
              :items="levelOptions"
              title="name"
              input-id="update-level-select"
              input-label="انتخاب سطح"
              :isLoading="levelsLoading"
              :isDisabled="levelsLoading || isSubmitting"
              :closeOnSelect="true"
              :isInDialog="true"
              class="custom-picker--level"
          >
            <template #labelOption="{ item }">
              <div class="level-option">
                <LevelAvatarBadge :avatarKey="item.avatar" />
                <div class="level-option__info">
                  <div class="level-option__name">{{ item.name }}</div>
                  <div class="level-option__meta">
                    <span class="meta-tag">اولویت {{ formatNumber(item.priority) }}</span>
                    <span class="meta-tag meta-tag--accent">{{ formatNumber(item.discount) }}%</span>
                  </div>
                </div>
              </div>
            </template>

            <template #selectedOption="{ item }">
              <div class="level-selected">
                <LevelAvatarBadge :avatarKey="item.avatar" size="sm" />
                <span class="level-selected__name">{{ item.name }}</span>
              </div>
            </template>

            <template #no-options>
              هیچ سطحی یافت نشد
            </template>
          </CustomPicker>

          <div v-if="touched && !levelValid" class="f-error">لطفاً سطح جدید را انتخاب کنید</div>
        </div>
      </b-col>

      <b-col cols="12">
        <div class="f-group">
          <label class="f-label required">
            <feather-icon icon="FileTextIcon" size="14" />
            توضیحات
          </label>

          <b-form-textarea
              v-model.trim="description"
              class="f-textarea"
              rows="3"
              placeholder="توضیحات...."
              :disabled="isSubmitting"
          />

          <div class="field-footer">
            <div v-if="touched && !descValid" class="f-error">وارد کردن توضیحات الزامی است</div>
            <div class="field-hint">{{ description.length }}/500</div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Footer -->
    <template #modal-footer>
      <div class="modal-footer-buttons">
        <b-button
            variant="outline-secondary"
            class="modal-btn modal-btn--ghost"
            @click="handleCancel"
            :disabled="isSubmitting"
        >
          <feather-icon icon="XIcon" size="16" />
          انصراف
        </b-button>

        <b-button
            variant="primary"
            class="modal-btn modal-btn--primary"
            @click="handleSubmit"
            :disabled="isSubmitting || !isValid"
        >
          <b-spinner v-if="isSubmitting" small />
          <feather-icon v-else icon="CheckIcon" size="16" />
          <span>{{ isSubmitting ? "در حال ذخیره..." : "ذخیره تغییرات" }}</span>
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BButton, BFormTextarea, BRow, BCol, BSpinner } from "bootstrap-vue";
import CustomPicker from "@/views/components/Utils/CustomPicker.vue";
import LevelAvatarBadge from "@core/components/loyalty/LevelAvatarBadge.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { List as GetLevelList, UpdateLevelLoyaltUserByAdmin } from "@/libs/Api/LevelLoyalty";

export default {
  name: "UpdateLevelModal",
  components: {
    BModal,
    BButton,
    BFormTextarea,
    BRow,
    BCol,
    BSpinner,
    CustomPicker,
    LevelAvatarBadge,
  },
  props: {
    value: { type: Boolean, default: false },
    userData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      selectedLevel: null,
      description: "",
      levelOptions: [],
      levelsLoading: false,
      isSubmitting: false,
      touched: false,
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    formData() {
      return {
        levelLoyaltUserId: Number(this.userData?.levelLoyaltUserId || 0),
        userName: String(this.userData?.fullName || this.userData?.userName || "—"),
        userMobile: String(this.userData?.mobile || "—"),
        currentLevelName: String(this.userData?.currentLevelName || this.userData?.levelName || "—"),
        currentLevelId: Number(this.userData?.currentLevelId || this.userData?.levelId || 0),
        currentLevelAvatar: String(this.userData?.currentLevelAvatar || this.userData?.levelAvatar || ""),
      };
    },
    levelValid() {
      return this.selectedLevel && Number(this.selectedLevel.id || 0) > 0;
    },
    descValid() {
      return String(this.description || "").trim().length >= 2;
    },
    isValid() {
      return this.formData.levelLoyaltUserId > 0 && this.levelValid && this.descValid;
    },
  },
  watch: {
    value: {
      handler(v) {
        if (v) {
          // opening modal: reset touched, clear description, load levels
          this.touched = false;
          this.description = "";
          this.loadLevels();
        } else {
          // if modal closed by parent, clear selected if needed (keeps UX predictable)
          // leave selectedLevel as-is so closing <-> reopening keeps selection; remove if not desired
        }
      },
      immediate: true,
    },
    description(v) {
      const s = String(v || "");
      if (s.length > 500) this.description = s.slice(0, 500);
    },
  },
  methods: {
    async loadLevels() {
      try {
        this.levelsLoading = true;
        const request = new GetLevelList(this);
        request.setRequestParam({ pageNumber: 1, count: 100 });
        const response = await this.requestAsPromise(request);
        const rows = response?.data?.data;
        this.levelOptions = Array.isArray(rows)
            ? rows.map((x) => ({
              id: Number(x.id || 0),
              name: String(x.name || ""),
              avatar: String(x.avatar || ""),
              priority: Number(x.priority || 0),
              discount: Number(x.discount || 0),
            }))
            : [];

        // select current if available
        if (this.levelOptions.length) {
          const current = this.levelOptions.find((o) => o.id === this.formData.currentLevelId) || null;
          this.selectedLevel = current;
        }
      } catch (e) {
        console.error(e);
        this.levelOptions = [];
        this.showToast("danger", "خطا", "خطا در دریافت لیست سطوح.");
      } finally {
        this.levelsLoading = false;
      }
    },

    async handleSubmit() {
      this.touched = true;
      if (!this.isValid || this.isSubmitting) return;

      this.isSubmitting = true;
      try {
        const payload = {
          levelLoyaltUserId: Number(this.formData.levelLoyaltUserId),
          levelLoyaltId: Number(this.selectedLevel.id),
          description: String(this.description || "").trim(),
        };

        const request = new UpdateLevelLoyaltUserByAdmin(this);
        request.setRequestParamDataObj(payload);
        const response = await this.requestAsPromise(request);

        if (!response || response.isSuccess === false) {
          const msg = response?.errors || response?.message || "عملیات انجام نشد.";
          this.showToast("danger", "خطا", msg);
          return;
        }

        this.showToast("success", "موفق", "سطح وفاداری با موفقیت به‌روزرسانی شد.");
        this.$emit("updated");
        this.isOpen = false;
      } catch (e) {
        console.error(e);
        this.showToast("danger", "خطا", "خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.");
      } finally {
        this.isSubmitting = false;
      }
    },

    handleCancel() {
      if (!this.isSubmitting) {
        this.isOpen = false;
      }
    },

    handleClose() {
      this.$emit("close");
    },

    requestAsPromise(request) {
      return new Promise((resolve, reject) => request.send(resolve, reject));
    },

    showToast(variant, title, text) {
      this.$toast({
        component: ToastificationContent,
        position: "bottom-center",
        props: { title, icon: "", variant, text },
      });
    },

    formatNumber(value) {
      const n = Number(value || 0);
      try {
        return new Intl.NumberFormat("fa-IR").format(n);
      } catch {
        return String(n);
      }
    },
  },
};
</script>

<style scoped>
/* Modal surface */
.update-level-modal :deep(.modal-content) {
  border: none;
  border-radius: 24px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  direction: rtl;
  position: relative; /* reference for dropdown positioning */
}

/* Header / body / footer paddings */
.modal-header {
  padding: 18px 20px;
  border-bottom: 2px solid #f3f4f6;
  background: #fff;
}
.modal-body {
  padding: 18px 20px;
  background: #fff;
}
.modal-footer {
  padding: 14px 20px;
  border-top: 2px solid #f3f4f6;
  background: #fff;
}

/* Title bar */
.modal-titlebar {
  display: flex;
  align-items: center;
  gap: 12px;
}
.modal-titlebar__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;
  flex: 0 0 auto;
}
.modal-titlebar__text {
  font-size: 18px;
  font-weight: 900;
  color: #111827;
}

/* User info card */
.user-info-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  margin-bottom: 20px;
  border-radius: 16px;
  border: 2px solid #e5e7eb;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.06), rgba(16, 185, 129, 0.05));
}
.user-info__left { display: flex; align-items: center; gap: 12px; min-width: 0; }
.user-info__avatar {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #111827;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}
.user-info__details { min-width: 0; }
.user-info__name {
  font-size: 14px; font-weight: 900; color: #111827;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 220px;
}
.user-info__mobile {
  margin-top: 2px; font-size: 12px; font-weight: 800; color: #6b7280; direction: ltr; text-align: right;
}
.user-info__right { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; flex: 0 0 auto; }
.user-info__current-label { font-size: 11px; font-weight: 900; color: #6b7280; }
.user-info__current { display: flex; align-items: center; gap: 8px; }
.user-info__current-value { font-size: 13px; font-weight: 900; color: #111827; white-space: nowrap; }

/* Form groups */
.f-group { margin-bottom: 20px; }
.f-label { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 900; color: #374151; margin-bottom: 8px; }
.f-label.required::after { content: "*"; color: #dc2626; margin-right: 4px; }
.f-error { font-size: 12px; font-weight: 700; color: #dc2626; margin-top: 8px; }

/* Textarea */
.f-textarea {
  border-radius: 14px;
  border: 2px solid #e5e7eb;
  font-weight: 600;
  font-size: 14px;
  padding: 12px 16px;
  transition: all 0.2s ease;
  resize: vertical;
  width: 100%;
}
.f-textarea:focus {
  border-color: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.08);
  outline: none;
}
.field-footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 8px; }
.field-hint { font-size: 11px; font-weight: 800; color: #9ca3af; direction: ltr; text-align: left; }

/* CustomPicker / dropdown visuals */
.custom-picker--level :deep(.vs__dropdown-toggle) {
  height: 48px;
  border-radius: 14px;
  border: 2px solid #e5e7eb;
  padding: 0 12px;
  background: #fff;
  transition: all 0.2s ease;
}
.custom-picker--level :deep(.vs__dropdown-toggle:focus-within),
.custom-picker--level :deep(.vs--open .vs__dropdown-toggle) {
  border-color: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.12);
}
.custom-picker--level :deep(.vs__dropdown-menu) {
  border-radius: 14px;
  border: 2px solid #e5e7eb;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.16);
  padding: 8px;
  margin-top: 6px;
  max-height: min(320px, 45vh);
  overflow-y: auto;
  position: absolute;
  right: 0;
  left: auto;
  z-index: 9999999; /* ensure above modal content/backdrop */
}

/* Level option */
.level-option { display: flex; align-items: center; gap: 12px; min-width: 0; padding: 6px 4px; }
.level-option__info { flex: 1; min-width: 0; }
.level-option__name { font-size: 13px; font-weight: 900; color: #111827; margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.level-option__meta { display: flex; gap: 6px; flex-wrap: wrap; }
.meta-tag { display: inline-flex; align-items: center; height: 20px; padding: 0 8px; border-radius: 8px; background: rgba(107,114,128,0.08); font-size: 11px; font-weight: 800; color: #4b5563; }
.meta-tag--accent { background: linear-gradient(135deg, rgba(34,197,94,0.12), rgba(16,185,129,0.1)); color: #16a34a; }

/* Selected */
.level-selected { display: flex; align-items: center; gap: 10px; min-width: 0; }
.level-selected__name { font-size: 13px; font-weight: 900; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 240px; }

/* Footer buttons */
.modal-footer-buttons { width: 100%; display: flex; justify-content: flex-end; gap: 12px; }
.modal-btn {
  height: 48px; border-radius: 14px; font-weight: 900; padding: 0 18px; display: inline-flex; align-items: center; justify-content: center; gap: 10px; min-width: 140px; transition: all 0.2s ease;
}
.modal-btn--ghost { background: transparent; border: 2px solid #e5e7eb; color: #6b7280; }
.modal-btn--primary { background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%); border: none; color: #fff; box-shadow: 0 6px 18px rgba(34, 197, 94, 0.28); }

/* Modal body internal scroll */
.update-level-modal :deep(.modal-body) {
  max-height: 60vh;
  overflow-y: auto;
  position: relative;
}
</style>